// src/App.js (Remove PDF.js configuration)
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';

// Layout Components
import Navbar from './components/Layout/Navbar';
import ErrorBoundary from './components/Layout/ErrorBoundary';
import ProtectedRoute from './components/Auth/ProtectedRoute';

// Auth Components
import Login from './components/Auth/Login';
import LoginModal from './components/Auth/LoginModal';
import Signup from './components/Auth/SignUp';
import ForgotPassword from './components/Auth/ForgotPassword';

// Page Components
import Dashboard from './components/Dashboard/Dashboard';
import DocTools from './components/PDF/PDFTools';
import DocumentViewer from './components/Documents/DocumentViewer';
import Settings from './components/Settings/Settings';
import Profile from './components/Profile/Profile';
import NotFound from './components/Layout/NotFound';

import BookletList from './components/Booklet/BookletList';
import BookletCreator from './components/Booklet/BookletCreator';
import BookletEditor from './components/Booklet/BookletEditor';
import BookletViewer from './components/Booklet/BookletViewer';

import PresentationList from './components/Presentation/PresentationList';
import PresentationCreator from './components/Presentation/PresentationCreator';
import PresentationEditor from './components/Presentation/PresentationEditor';
import PresentationPreview from './components/Presentation/PresentationPreview';
import LivePresentation from './components/Presentation/LivePresentation';
import JoinSession from './components/Presentation/JoinSession';
import FormsList from './components/forms/FormsList';
import ConversionPage from './components/forms/ConversionPage';
import FormViewer from './components/forms/FormViewer';
import SmartProcessingStudio from './components/forms/SmartProcessingStudio';
import SheetsList from './components/sheets/SheetsList';
import ParrotSheetsPricingPage from './components/sheets/ParrotSheetsPricingPage';
import SheetBuilder from './components/sheets/SheetBuilder';
import SheetFormPage from './components/sheets/SheetFormPage';
import SheetViewer from './components/sheets/SheetViewer';
import WorkspacesList from './components/sheets/WorkspacesList';
import SheetsAdminAccess from './components/sheets/SheetsAdminAccess';
import OrganizationsPage from './components/sheets/OrganizationsPage';
import WorkspaceBuilder from './components/sheets/WorkspaceBuilder';
import WorkspacePage from './components/sheets/WorkspacePage';
import { isDefaultSheetsHost, isLikelyWorkspaceCustomHost } from './utils/sheetDomains';
import { applyDocumentBranding, getActiveBrand } from './utils/productBranding';
import { useAuth } from './contexts/AuthContext';

// Styles
import './App.css';

function ModalProtectedRoute({ children, logo = 'sheets' }) {
  const { user, loading } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      setIsLoginModalOpen(true);
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008080] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md rounded-3xl border border-[#d7dfd5] bg-white px-8 py-10 text-center shadow-[0_28px_70px_-42px_rgba(15,23,42,0.38)]">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#617767]">Workspace Access</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900">Sign in to continue</h1>
            <p className="mt-3 text-sm leading-6 text-slate-500">
              This workspace uses modal authentication. Sign in here to access the workspace and your profile.
            </p>
            <button
              type="button"
              onClick={() => setIsLoginModalOpen(true)}
              className="mt-6 inline-flex items-center rounded-2xl bg-[#008080] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f6d6d]"
            >
              Open Sign In
            </button>
          </div>
        </div>

        {isLoginModalOpen && (
          <LoginModal
            onClose={() => setIsLoginModalOpen(false)}
            onSuccess={() => setIsLoginModalOpen(false)}
            logo={logo}
          />
        )}
      </>
    );
  }

  return children;
}

function AppContent() {
  const location = useLocation();
  const isDefaultSheetsDomain = isDefaultSheetsHost(window.location.hostname);
  const isWorkspaceCustomDomain = isLikelyWorkspaceCustomHost(window.location.hostname);
  const isSheetViewerRoute =
    location.pathname.startsWith('/sheet/') ||
    /^\/workspace\/[^/]+\/sheet\/[^/]+/.test(location.pathname) ||
    /^\/sheets\/workspace\/[^/]+\/sheet\/[^/]+/.test(location.pathname);
  const isSheetFormRoute = location.pathname.startsWith('/sheet-form/');
  const isSheetRoute = isSheetViewerRoute || isSheetFormRoute;
  const isSheetsRoute = location.pathname.startsWith('/sheets');
  const isWorkspaceAliasRoute = location.pathname.startsWith('/workspace/');
  const isWorkspaceRoute = location.pathname.startsWith('/sheets/workspace/');
  const isSheetsSectionRoute =
    isSheetsRoute ||
    isWorkspaceRoute ||
    isWorkspaceAliasRoute ||
    isDefaultSheetsDomain ||
    isWorkspaceCustomDomain;
  const hideGlobalHeader =
    location.pathname.startsWith('/sheet/') ||
    location.pathname.startsWith('/sheet-form/') ||
    location.pathname.startsWith('/forms/smart') ||
    isSheetsSectionRoute;

  useEffect(() => {
    const brand = getActiveBrand({
      hostname: window.location.hostname,
      pathname: location.pathname
    });
    applyDocumentBranding(brand, {
      url: window.location.href
    });
  }, [location.pathname]);

  return (
    <div className={(isSheetRoute || isSheetsSectionRoute) ? 'min-h-screen bg-gradient-to-br from-[#f6f5f5] via-white to-[#008080]/10' : 'min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50'}>
      {!hideGlobalHeader && <Navbar />}
            
            <main className={isSheetViewerRoute ? 'h-screen overflow-hidden px-0 py-0 sm:px-2' : isSheetFormRoute ? 'min-h-screen px-0 py-0' : 'container mx-auto max-w-7xl px-2 py-4'}>
              <Routes>
                {/* Public Routes */}

                <Route
                  path="/"
                  element={
                    isWorkspaceCustomDomain ? (
                      <ModalProtectedRoute logo="sheets">
                        <WorkspacePage />
                      </ModalProtectedRoute>
                    ) : isDefaultSheetsDomain ? (
                      <SheetsList />
                    ) : (
                      <Navigate to="/doc-tools" replace />
                    )
                  }
                />

                <Route path="/doc-tools" element={<DocTools />} />
                <Route path="/pdf-tools" element={<Navigate to="/doc-tools" replace />} />


                <Route 
                  path="/login" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Login />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/signup" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Signup />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/forgot-password" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ForgotPassword />
                    </ProtectedRoute>
                  } 
                />

                {/* Public Booklet Viewer (no auth required) */}
                <Route 
                  path="/booklet/:id" 
                  element={<BookletViewer />} 
                />

                {/* Public Form Viewer (no auth required) */}
                <Route 
                  path="/form/:id" 
                  element={<FormViewer />} 
                />

                <Route
                  path="/sheet-form/:id"
                  element={<SheetFormPage />}
                />

                <Route
                  path="/sheet/:id"
                  element={<SheetViewer />}
                />
                <Route
                  path="/workspace/:workspaceSlug/sheet/:id"
                  element={<SheetViewer />}
                />
                <Route
                  path="/sheets/workspace/:workspaceSlug/sheet/:id"
                  element={<SheetViewer />}
                />

                {/* Protected Routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/dashboard" 
                  element={<Navigate to="/" replace />} 
                />
              
                <Route 
                  path="/document/:id" 
                  element={
                    <ProtectedRoute>
                      <DocumentViewer />
                    </ProtectedRoute>
                  } 
                />

                {/* Forms Routes */}
                <Route 
                  path="/forms" 
                  element={
                    <ProtectedRoute>
                      <FormsList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/forms/convert" 
                  element={
                    <ProtectedRoute>
                      <ConversionPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/forms/smart" 
                  element={
                    <ProtectedRoute>
                      <SmartProcessingStudio />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/forms/smart/:id/edit" 
                  element={
                    <ProtectedRoute>
                      <SmartProcessingStudio />
                    </ProtectedRoute>
                  } 
                />

                <Route
                  path="/workspaces"
                  element={
                    isDefaultSheetsDomain ? (
                      <ProtectedRoute>
                        <WorkspacesList />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/sheets/workspaces" replace />
                    )
                  }
                />
                <Route
                  path="/workspaces/new"
                  element={
                    isDefaultSheetsDomain ? (
                      <ProtectedRoute>
                        <WorkspaceBuilder />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/sheets/workspaces/new" replace />
                    )
                  }
                />
                <Route
                  path="/workspaces/:workspaceSlug/edit"
                  element={
                    isDefaultSheetsDomain ? (
                      <ProtectedRoute>
                        <WorkspaceBuilder />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to={`/sheets${location.pathname}`} replace />
                    )
                  }
                />
                <Route
                  path="/workspace/:workspaceSlug"
                  element={
                    <ProtectedRoute>
                      <WorkspacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workspace/:workspaceSlug/new"
                  element={
                    <ProtectedRoute>
                      <SheetBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sheets/workspaces"
                  element={
                    <ProtectedRoute>
                      <WorkspacesList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sheets/workspaces/new"
                  element={
                    <ProtectedRoute>
                      <WorkspaceBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sheets/workspaces/:workspaceSlug/edit"
                  element={
                    <ProtectedRoute>
                      <WorkspaceBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sheets/workspace/:workspaceSlug"
                  element={
                    <ProtectedRoute>
                      <WorkspacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sheets/workspace/:workspaceSlug/new"
                  element={
                    <ProtectedRoute>
                      <SheetBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/new"
                  element={
                    isWorkspaceCustomDomain ? (
                      <ModalProtectedRoute logo="sheets">
                        <SheetBuilder />
                      </ModalProtectedRoute>
                    ) : isDefaultSheetsDomain ? (
                      <ProtectedRoute>
                        <SheetBuilder />
                      </ProtectedRoute>
                    ) : (
                      <Navigate to="/sheets/new" replace />
                    )
                  }
                />
                <Route
                  path="/sheets"
                  element={<SheetsList />}
                />
                <Route
                  path="/sheets/pricing"
                  element={<ParrotSheetsPricingPage />}
                />
                <Route
                  path="/sheets/admin-access"
                  element={
                    <ProtectedRoute>
                      <SheetsAdminAccess />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sheets/organizations"
                  element={
                    <ProtectedRoute>
                      <OrganizationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pricing"
                  element={
                    isDefaultSheetsDomain ? (
                      <ParrotSheetsPricingPage />
                    ) : (
                      <Navigate to="/sheets/pricing" replace />
                    )
                  }
                />
                <Route
                  path="/sheets/new"
                  element={
                    <ProtectedRoute>
                      <SheetBuilder />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sheets/:id/edit"
                  element={
                    <ProtectedRoute>
                      <SheetBuilder />
                    </ProtectedRoute>
                  }
                />

                {/* Booklet Routes */}
                <Route 
                  path="/booklets" 
                  element={
                    <ProtectedRoute>
                      <BookletList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/booklet/create" 
                  element={
                    <ProtectedRoute>
                      <BookletCreator />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/booklet/:id/edit" 
                  element={
                    <ProtectedRoute>
                      <BookletEditor />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/presentations" 
                  element={
                    <ProtectedRoute>
                      <PresentationList />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/presentation/create" 
                  element={
                    <ProtectedRoute>
                      <PresentationCreator />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/presentation/:id/edit" 
                  element={
                    <ProtectedRoute>
                      <PresentationEditor />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/presentation/:id" 
                  element={
                    <PresentationPreview />
                  } 
                />
                <Route 
                  path="/presentation/:id/present" 
                  element={
                    <ProtectedRoute>
                      <LivePresentation />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/join/:sessionCode" 
                  element={
                    <JoinSession />
                  } 
                />
                
                <Route 
                  path="/settings" 
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Fallback Routes */}
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </main>

            {/* Global Toast Notifications */}
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                  borderRadius: '0.5rem',
                  fontSize: '14px',
                  maxWidth: '500px',
                },
                success: {
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
